<template>
  <form @submit.prevent="onSubmit" novalidate>
    <div class="grid grid-cols-2 gap-4">
      <div class="col-span-2 sm:col-span-1">
        <TextInput
          class="mb-3"
          placeholder="First Name"
          v-model="v$.first_name.$model"
          :icon="faUser"
          :errors="v$?.first_name?.$errors[0]?.$message"
        />
      </div>
      <div class="col-span-2 sm:col-span-1">
        <TextInput
          class="mb-3"
          placeholder="Last Name"
          v-model="v$.last_name.$model"
          :icon="faUser"
          :errors="v$?.last_name?.$errors[0]?.$message"
        />
      </div>
      <div class="col-span-2">
        <TextInput
          class="mb-3"
          placeholder="Enter Email"
          v-model="v$.email.$model"
          :icon="faEnvelope"
          :errors="v$?.email?.$errors[0]?.$message"
        />
      </div>
      <div class="col-span-2 sm:col-span-1">
        <TextInput
          type="password"
          v-model="v$.password.$model"
          placeholder="Enter Password"
          :icon="faLock"
          :errors="v$?.password?.$errors[0]?.$message"
        />
        <p class="text-sm text-red-500" v-if="passError">{{ passError }}</p>
      </div>
      <div class="col-span-2 sm:col-span-1">
        <TextInput
          type="password"
          class="mb-5"
          v-model="v$.confirm_password.$model"
          placeholder="Confirm Password"
          :icon="faLock"
          :errors="v$?.confirm_password?.$errors[0]?.$message"
        />
      </div>
    </div>
    <SubmitButton class="px-20 mx-auto mb-5" :isLoading="isLoading"
      >Register</SubmitButton
    >

    <!-- Login Link -->
    <div class="text-center mb-5">
      <p class="text-gray-600">
        Already have an account?
        <router-link to="/login" class="text-custom-green font-semibold hover:underline">
          Login
        </router-link>
      </p>
    </div>

    <ul
      v-if="errors.api"
      class="grid-cols-2 p-4 font-medium text-red-800 rounded-md bg-red-50"
    >
      <li v-for="(errors, field) in errors.api" :key="field">
        <strong>{{ field }}:</strong>
        <ul>
          <li v-for="(error, index) in errors" :key="index">- {{ error }}</li>
        </ul>
      </li>
    </ul>
  </form>
</template>
<script>
import {
  faEnvelope,
  faLock,
  faUser
} from '@fortawesome/free-solid-svg-icons'
import { reactive, ref } from '@vue/reactivity'
import { useVuelidate } from '@vuelidate/core'
import {
  email,
  helpers,
  minLength,
  required
} from '@vuelidate/validators'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SubmitButton from '../SubmitButton.vue'
import TextInput from '../TextInput.vue'

export default {
  components: { TextInput, SubmitButton },
  setup () {
    const isLoading = ref(false)
    const passError = ref('')
    const errors = ref('')
    const store = useStore()
    const router = useRouter()

    const state = reactive({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirm_password: ''
    })

    const passwordValidator = (value) => {
      const regex =
        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/
      return regex.test(value)
    }

    const rules = {
      first_name: {
        required: helpers.withMessage('First name is required.', required)
      },
      last_name: {
        required: helpers.withMessage('Last name is required.', required)
      },
      email: {
        required: helpers.withMessage('Email field is required', required),
        email: helpers.withMessage(
          'Email should be a valid email address ',
          email
        )
      },
      password: {
        required: helpers.withMessage('Password field is required', required),
        minLength: minLength(8),
        passwordValidator: helpers.withMessage(
          'Password must contain at least one uppercase letter, one number, and one special character.',
          passwordValidator
        )
      },
      confirm_password: {
        required: helpers.withMessage(
          'Confirm Password field is required',
          required
        )
      }
    }
    const v$ = useVuelidate(rules, state)

    const onSubmit = () => {
      passError.value = ''
      v$.value.$touch()

      if (v$.value.$invalid) return

      // Check if passwords match
      if (state.password !== state.confirm_password) {
        passError.value = 'Password and confirm password does not match'
        return
      }

      isLoading.value = true
      errors.value = null

      // Include confirm_password in the API request
      const requestData = {
        first_name: state.first_name,
        last_name: state.last_name,
        email: state.email,
        password: state.password,
        confirm_password: state.confirm_password
      }

      store
        .dispatch('auth/SignUp', requestData)
        .then(
          (data) => {
            router.push({
              name: 'Success'
            })
          },
          (error) => {
            if (error.response.status === 500) {
              errors.value = 'Something went wrong please try again.'
            } else if (error.response.status === 400) {
              errors.value = { api: error.response.data.errors }
            } else {
              errors.value =
                (error.response &&
                  error.response.data &&
                  error.response.data.message) ||
                error.message ||
                error ||
                error.toString()
            }
          }
        )
        .then(function () {
          isLoading.value = false
        })
    }
    return {
      onSubmit,
      v$,
      faEnvelope,
      faLock,
      state,
      errors,
      passError,
      faUser,
      isLoading
    }
  }
}
</script>

<style>
/* Mobile-specific styles */
@media (max-width: 768px) {
  select {
    font-size: 1.25rem; /* Larger font size */
    padding: 1rem; /* Increased padding */
  }

  option {
    font-size: 1.25rem; /* Larger font size for options */
    padding: 0.75rem; /* Extra spacing for better touch usability */
  }
}
</style>
